import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  ShieldCheck, 
  MessageSquareWarning, 
  FileText, 
  LifeBuoy, 
  ArrowRight, 
  CheckCircle2, 
  Lock, 
  Sparkles,
  Layers,
  ChevronRight
} from 'lucide-react';

export const HowItWorksPage: React.FC = () => {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-14">
      {/* Header */}
      <div className="text-center space-y-3 max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-semibold border border-emerald-800">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Product Architecture & Methodology</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          How IDFraud.au Protects Australians
        </h1>
        <p className="text-slate-400 text-sm">
          A methodical, four-stage personal identity security framework: Detect → Understand → Protect → Recover.
        </p>
      </div>

      {/* User Journey Step-by-Step Breakdown (Section 35) */}
      <div className="space-y-8">
        {/* Stage 1 */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-start gap-6 shadow-xl">
          <div className="w-12 h-12 rounded-2xl bg-emerald-950 border border-emerald-800 text-emerald-400 flex items-center justify-center font-extrabold text-lg flex-shrink-0">
            01
          </div>
          <div className="space-y-3 flex-1">
            <span className="text-xs uppercase font-bold tracking-wider text-emerald-400">Step 1: Rapid Detection</span>
            <h3 className="text-xl font-bold text-white">Search Historical & Live Data Breaches</h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              Enter your email address, Australian mobile number, or username into our exposure engine. We query millions of compromised corporate databases (including Optus, Medibank, Ticketmaster, Canva) using privacy-preserving k-anonymity cryptographic hashing without ever asking for your passwords.
            </p>
            <Link to="/check-identity" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-400 hover:underline">
              <span>Try Identity Exposure Checker</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Stage 2 */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-start gap-6 shadow-xl">
          <div className="w-12 h-12 rounded-2xl bg-blue-950 border border-blue-800 text-blue-400 flex items-center justify-center font-extrabold text-lg flex-shrink-0">
            02
          </div>
          <div className="space-y-3 flex-1">
            <span className="text-xs uppercase font-bold tracking-wider text-blue-400">Step 2: Understand the Risk</span>
            <h3 className="text-xl font-bold text-white">AI Scam & Phishing Intelligence</h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              When you receive a suspicious text (e.g. Linkt overdue tolls, Australia Post redelivery fees, or myGov tax refund links), our AI Scam Engine breaks down the psychological urgency, identifies non-official domain spoofing, and checks for known impersonation vectors.
            </p>
            <Link to="/scam-checker" className="inline-flex items-center gap-1 text-xs font-bold text-blue-400 hover:underline">
              <span>Open AI Scam Checker</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Stage 3 */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-start gap-6 shadow-xl">
          <div className="w-12 h-12 rounded-2xl bg-teal-950 border border-teal-800 text-teal-400 flex items-center justify-center font-extrabold text-lg flex-shrink-0">
            03
          </div>
          <div className="space-y-3 flex-1">
            <span className="text-xs uppercase font-bold tracking-wider text-teal-400">Step 3: Document Hardening</span>
            <h3 className="text-xl font-bold text-white">Protect Australian Identity Credentials</h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              We monitor your Australian Driver Licence Card Number against Document Verification Service (DVS) misuse, track Medicare card reissues, and provide an in-browser ID Watermark Generator so rental agencies and employers only receive stamped copies.
            </p>
            <Link to="/identity-documents" className="inline-flex items-center gap-1 text-xs font-bold text-teal-400 hover:underline">
              <span>View Australian ID Guides</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Stage 4 */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-start gap-6 shadow-xl">
          <div className="w-12 h-12 rounded-2xl bg-rose-950 border border-rose-800 text-rose-400 flex items-center justify-center font-extrabold text-lg flex-shrink-0">
            04
          </div>
          <div className="space-y-3 flex-1">
            <span className="text-xs uppercase font-bold tracking-wider text-rose-400">Step 4: Rapid Incident Response</span>
            <h3 className="text-xl font-bold text-white">8-Step Emergency Recovery Wizard</h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              If an identity theft occurs, our emergency wizard guides you through freezing credit bureau files (Equifax, Experian, illion), disputing fraudulent transactions with banks under the ePayments Code, and obtaining free case support from IDCARE (1800 595 160).
            </p>
            <Link to="/identity-theft" className="inline-flex items-center gap-1 text-xs font-bold text-rose-400 hover:underline">
              <span>Explore Emergency Recovery</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Box */}
      <div className="p-8 rounded-3xl bg-gradient-to-r from-emerald-950 via-slate-900 to-slate-900 border border-emerald-800/80 text-center space-y-4 shadow-2xl">
        <h3 className="text-2xl font-bold text-white">Ready to take control of your identity?</h3>
        <p className="text-slate-300 text-xs max-w-md mx-auto">
          Start with a free breach check in 30 seconds. No credit card required.
        </p>
        <Link
          to="/check-identity"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-lg shadow-emerald-950/40 transition-colors"
        >
          <span>Check My Identity Now</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};
